import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { SEO } from '../../utils/seo';

export default function AboutPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "mainEntity": {
      "@type": "Organization",
      "name": "Greenfield Partners",
      "description": "We exist to help farmers protect their yield, manage their land, and make smarter decisions—using technology that works as hard as they do.",
      "foundingDate": "2020",
      "url": siteUrl
    }
  };

  const coreValues = [
    {
      icon: 'ri-shield-check-line',
      title: 'Safety First',
      description: 'Every flight, every field, every time. No shortcuts, no compromises.',
    },
    {
      icon: 'ri-hand-heart-line',
      title: 'Farmer-Focused',
      description: 'We work alongside you, not over you. Your land, your goals, our partnership.',
    },
    {
      icon: 'ri-focus-3-line',
      title: 'Precision Matters',
      description: 'Accurate application, reliable data, and results you can measure.',
    },
    {
      icon: 'ri-timer-line',
      title: 'Timing Is Everything',
      description: 'We show up when it counts—because in farming, windows don\'t wait.',
    },
  ];

  const expertise = [
    {
      icon: 'ri-plane-line',
      title: 'Advanced Flight Operations',
      description: 'FAA Part 107 certified pilots with specialized agricultural training and thousands of flight hours across diverse terrain.',
    },
    {
      icon: 'ri-database-2-line',
      title: 'Data-Driven Insights',
      description: 'RGB, NDVI, and thermal imaging capabilities that turn aerial data into actionable intelligence for your operation.',
    },
    {
      icon: 'ri-drop-line',
      title: 'Precision Application',
      description: 'Variable-rate technology and GPS-guided systems that put product exactly where it needs to be—and nowhere else.',
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Land Management',
      description: 'Beyond spraying: woodland assessment, deer pressure mitigation, and comprehensive land stewardship services.',
    },
  ];

  const differentiators = [
    {
      number: '01',
      title: 'We\'re Farmers First',
      description: 'We didn\'t start as a tech company looking for a market. We started in the field, solving real problems for real operations. That perspective shapes everything we do.',
    },
    {
      number: '02',
      title: 'No Wheel Traffic',
      description: 'Our aerial approach means zero soil compaction, no crop damage from equipment, and access to fields when ground rigs can\'t get in.',
    },
    {
      number: '03',
      title: 'Integrated Services',
      description: 'Spraying, surveying, and land management under one roof. We see the whole picture because we\'re already flying your fields.',
    },
    {
      number: '04',
      title: 'Local Presence',
      description: 'We\'re not dispatching from a distant hub. Our pilots know your region, your conditions, and your challenges.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO
        title="About Greenfield Partners - Built for the Land, Driven by Results"
        description="We exist to help farmers protect their yield, manage their land, and make smarter decisions—using technology that works as hard as they do."
        keywords="agricultural drone company, precision farming experts, FAA certified pilots, agricultural technology"
        schema={schema}
      />

      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center">
        <div className="absolute inset-0 bg-gray-900">
          <img
            src="https://static.readdy.ai/image/f53427c02b00678782e68874888578ce/bb83daf29117779be8441f2ca6ed4aa5.png"
            alt="Evolution of agriculture - traditional tractor sprayer transitioning to modern precision drone technology"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32">
          <p className="text-emerald-400 font-semibold tracking-wide mb-4">ABOUT GREENFIELD PARTNERS</p>
          <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight mb-6 max-w-3xl">
            Built for the Land.<br />Driven by Results.
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl leading-relaxed">
            We exist to help farmers protect their yield, manage their land, and make smarter decisions—using technology that works as hard as they do.
          </p>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Mission Section */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-emerald-800 font-semibold tracking-wide mb-4">OUR MISSION</p>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Partnering With Farmers to Maximize Every Acre
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Greenfield Partners was founded on a simple belief: farmers deserve better tools, better timing, and better partners. Not vendors who show up once and disappear—but a team that understands the land and the stakes.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                We combine advanced drone technology with practical agricultural knowledge to deliver precision application, actionable field data, and comprehensive land management services. Every service we offer is designed around one goal: protecting and improving your yield.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                This isn't about drones. It's about outcomes. It's about being there when the window opens, putting product where it matters, and giving you the information you need to make confident decisions.
              </p>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/39761c24d53b45f0b64bd1dcc6209529.jpg"
                  alt="Drone pilot preparing for field operation"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 bg-emerald-700 text-white p-6 rounded-lg shadow-lg max-w-xs">
                <p className="text-3xl font-bold mb-1">10,000+</p>
                <p className="text-emerald-100">Acres served annually</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Core Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-emerald-800 font-semibold tracking-wide mb-4">CORE VALUES</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              What We Stand For
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              These aren't just words on a wall. They're the principles that guide every flight, every conversation, and every decision we make.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {coreValues.map((value, index) => (
              <div
                key={index}
                className="bg-stone-50 rounded-lg p-8 text-center hover:shadow-lg transition-shadow"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-6">
                  <i className={`${value.icon} text-3xl text-emerald-800`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Expertise Section - Add links to service pages */}
      <section className="py-24 topo-cta-bg text-white relative">
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <p className="text-emerald-400 font-semibold tracking-wide mb-4">OUR EXPERTISE</p>
              <h2 className="text-4xl font-bold mb-6">
                Advanced Capabilities.<br />Practical Application.
              </h2>
              <p className="text-lg text-gray-300 leading-relaxed mb-8">
                We've invested in the technology, training, and talent to deliver results that matter. But capability without context is just noise. Our expertise is grounded in understanding what farmers actually need—and delivering it reliably.
              </p>
              <div className="aspect-[16/9] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Agricultural%20drone%20flying%20low%20over%20green%20crop%20field%20spraying%20mist%20precision%20application%20technology%20professional%20commercial%20drone%20operation%20dramatic%20lighting%20cinematic%20style&width=800&height=450&seq=about-expertise-1&orientation=landscape"
                  alt="Precision drone application over crops"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="space-y-6">
              {[
                {
                  icon: 'ri-plane-line',
                  title: 'Advanced Flight Operations',
                  description: 'FAA Part 107 certified pilots with specialized agricultural training and thousands of flight hours across diverse terrain.',
                  link: null
                },
                {
                  icon: 'ri-database-2-line',
                  title: 'Data-Driven Insights',
                  description: 'RGB, NDVI, and thermal imaging capabilities that turn aerial data into actionable intelligence for your operation.',
                  link: '/services/agriculture'
                },
                {
                  icon: 'ri-drop-line',
                  title: 'Precision Application',
                  description: 'Variable-rate technology and GPS-guided systems that put product exactly where it needs to be—and nowhere else.',
                  link: '/services/agriculture'
                },
                {
                  icon: 'ri-map-pin-line',
                  title: 'Land Management',
                  description: 'Beyond spraying: woodland assessment, deer pressure mitigation, and comprehensive land stewardship services.',
                  link: '/services/timber-forestry'
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-white/5 backdrop-blur-sm rounded-lg p-6 hover:bg-white/10 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-emerald-700 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-2xl text-white`}></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2">
                        {item.link ? (
                          <a href={item.link} className="hover:text-emerald-300 transition-colors">
                            {item.title}
                          </a>
                        ) : (
                          item.title
                        )}
                      </h3>
                      <p className="text-gray-400 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Industries We Serve */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-emerald-800 font-semibold tracking-wide mb-4">WHO WE SERVE</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Industries That Trust Us
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From row crops to timber, from livestock operations to conservation programs—we serve the people who work the land. <a href="/industries" className="text-emerald-800 hover:text-emerald-700 font-medium inline-flex items-center gap-1">Learn more about our industry expertise <i className="ri-arrow-right-line"></i></a>
            </p>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6">
            {[
              { icon: 'ri-plant-line', label: 'Row Crops', link: '/industries' },
              { icon: 'ri-seedling-line', label: 'Grain Production', link: '/industries' },
              { icon: 'ri-landscape-line', label: 'Livestock & Pasture', link: '/industries' },
              { icon: 'ri-tree-line', label: 'Timber & Woodland', link: '/industries' },
              { icon: 'ri-leaf-line', label: 'Specialty Crops', link: '/industries' },
              { icon: 'ri-earth-line', label: 'Conservation', link: '/industries' },
            ].map((industry, index) => (
              <a
                key={index}
                href={industry.link}
                className="bg-white rounded-lg p-6 text-center shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-emerald-50 rounded-full mx-auto mb-4">
                  <i className={`${industry.icon} text-2xl text-emerald-800`}></i>
                </div>
                <p className="font-semibold text-gray-900">{industry.label}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* What Sets Us Apart */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-emerald-800 font-semibold tracking-wide mb-4">THE DIFFERENCE</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              What Sets Us Apart
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              There are other drone operators out there. Here's why farmers choose to work with us.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {differentiators.map((item, index) => (
              <div
                key={index}
                className="flex gap-6 p-8 bg-stone-50 rounded-lg hover:shadow-lg transition-shadow"
              >
                <span className="text-5xl font-bold text-emerald-200">{item.number}</span>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Commitment Section */}
      <section className="py-24 topo-cta-bg text-white relative">
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-emerald-200 font-semibold tracking-wide mb-4">OUR COMMITMENT</p>
              <h2 className="text-4xl font-bold mb-6">
                Safety. Quality. Results.
              </h2>
              <p className="text-lg text-emerald-100 leading-relaxed mb-6">
                Every operation we run is built on a foundation of uncompromising safety standards, rigorous quality control, and a relentless focus on client success. We don't cut corners, and we don't make promises we can't keep.
              </p>
              <ul className="space-y-4">
                {[
                  'FAA Part 107 certified pilots with ongoing training',
                  'Comprehensive pre-flight safety protocols',
                  'Real-time weather monitoring and risk assessment',
                  'Full insurance coverage for every operation',
                  'Detailed documentation and reporting',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-check-line text-xl text-emerald-400"></i>
                    </div>
                    <span className="text-emerald-50">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20agricultural%20drone%20team%20reviewing%20flight%20data%20on%20tablet%20in%20field%20setting%20safety%20vests%20professional%20equipment%20warm%20golden%20hour%20lighting%20teamwork%20collaboration%20documentary%20style&width=800&height=600&seq=about-commitment-1&orientation=landscape"
                alt="Team reviewing flight data in the field"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Trust Statement & CTA */}
      <section className="py-24 bg-stone-100">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-20 h-20 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-8">
            <i className="ri-plant-line text-4xl text-emerald-800"></i>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            This Is Not a Drone Company.
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed mb-8">
            It's a farming partner. One that happens to fly. We're here to protect your yield, strengthen your land, and help you make decisions with confidence. When you work with Greenfield Partners, you're not hiring a vendor—you're gaining a team that's invested in your success.
          </p>
          <p className="text-lg text-gray-500 mb-10">
            Ready to see what a real partnership looks like?
          </p>
          <a
            href="/#talk"
            className="inline-block bg-emerald-800 hover:bg-emerald-700 text-white font-bold px-10 py-4 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-lg shadow-lg hover:shadow-xl"
          >
            Let's Start a Conversation
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
